import React, { ReactNode, useState } from 'react';
import OtpInput from 'react-otp-input';

import { defaultSubTitle } from '../../../hooks/useMFA/constants';
import { ElementWrapper, StyledBottomContent } from '../../../pages/Login/styled';
import { BlueButton, StyledLink } from '../../BlueTheme';
import { StyledCheckbox } from '../../BlueTheme/StyledCheckbox/styled';
import StepHeader from '../StepHeader/StepHeader';
import { ENTER_CODE_SYMBOLS_COUNT } from './constants';
import {
  CodeInputError,
  CodeInputLabel,
  CodeInputSeparator,
  CodeInputStyled,
  StyledCheckboxWrapper,
  VerificationCodeContainer,
} from './styled';

type Props = {
  loading: boolean;
  verifyCode: (smsCode: string) => void;
  resendCode: () => void;
  errorToShow: string;
  setErrorToShow: (errorToShow: string) => void;
  setupMfaFlow?: boolean;
  saveDevice?: boolean;
  setSaveDevice?: () => void;
};
const EnterCodeStep = ({
  loading,
  verifyCode,
  resendCode,
  errorToShow,
  setErrorToShow,
  setupMfaFlow = false,
  saveDevice,
  setSaveDevice,
}: Props): JSX.Element => {
  const [code, setCode] = useState('');
  const [codeTyped, setCodeTyped] = useState(false);

  const setFullCode = (code: string): void => {
    setCode(code);
    setCodeTyped(true);
    setErrorToShow('');
  };

  const getValidationError = (): string => {
    if (code.length === ENTER_CODE_SYMBOLS_COUNT || (!codeTyped && !code.length)) return '';
    if (codeTyped && !code.length) return 'Verification code is required';

    return 'Please enter valid verification code';
  };

  const errorText = errorToShow || getValidationError();

  const showError = !!errorText;

  const handleSubmit = (): void => {
    verifyCode(code);
  };

  return (
    <>
      <StepHeader subTitle={defaultSubTitle} />
      <VerificationCodeContainer data-testid="enterCodeWrapper">
        <CodeInputLabel $isError={showError}>
          {setupMfaFlow ? 'Verification Code' : 'Authentication Code'}
        </CodeInputLabel>
        <OtpInput
          shouldAutoFocus={true}
          inputStyle="otpCode"
          value={code}
          inputType="number"
          onChange={setFullCode}
          renderSeparator={<CodeInputSeparator />}
          numInputs={ENTER_CODE_SYMBOLS_COUNT}
          renderInput={(props): ReactNode => <CodeInputStyled $isError={showError} {...props} />}
        />
        {showError && <CodeInputError data-testid="fieldCodeError">{errorText}</CodeInputError>}
      </VerificationCodeContainer>
      <ElementWrapper>
        <BlueButton
          name={setupMfaFlow ? 'Verify' : 'Authenticate'}
          type="submit"
          onClick={handleSubmit}
          disabled={showError || loading || !code}
          isLoading={loading}
          data-testid="verifyCodeButton"
        />
      </ElementWrapper>
      {setSaveDevice && (
        <StyledCheckboxWrapper>
          <StyledCheckbox
            data-testid="rememberDevice"
            label="Remember this device"
            checked={saveDevice}
            onChange={setSaveDevice}
          />
        </StyledCheckboxWrapper>
      )}
      <StyledBottomContent>
        <p>
          <StyledLink onClick={resendCode} data-testid="resendCode">
            Resend Code
          </StyledLink>
        </p>
      </StyledBottomContent>
    </>
  );
};

export default EnterCodeStep;
